import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { GapFilterBanner } from '@/components/repGaps/GapFilterBanner'
import { repGapFor } from '@/lib/repGaps'
import {
  useTasks,
  useTaskStats,
  useUpdateTask,
  useDeleteTask,
  useBulkDeleteTasks,
  useBulkUpdateTasks,
  useBulkAssignTasks,
} from '@/hooks/use-api'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { ResizableTable } from '@/components/ui/resizable-table'
import { ColumnPicker } from '@/components/ui/column-picker'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import { useColumnOrder } from '@/hooks/use-column-order'
import { CalendarDays, Plus, CheckCircle2, Clock, Download, X } from 'lucide-react'
import type { Task as _Task } from '@/types'
import { exportTasksToCSV } from '@/lib/csv-export'
import { TaskDetailModal } from '@/components/TaskDetailModal'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { BulkAssignModal } from '@/components/BulkAssignModal'
import { createTasksColumns, tasksDefaultColumnSizing } from '@/config/columns/tasks-columns'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useReadOnly } from '@/hooks/use-read-only'
import { useDebounce } from '@/hooks/use-debounce'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { explainError } from '@/lib/explain-error'

export default function Tasks() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'tasks')
  const [search, setSearch] = useState('')
  // Debounced so typing doesn't fire one GET /tasks per keystroke.
  const debouncedSearch = useDebounce(search, 300)
  const [pageSize, setPageSize] = usePersistentState(fk('pageSize'), 50)
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  type TaskFilter = 'all' | 'my' | 'assigned'
  const [taskFilter, setTaskFilter] = usePersistentState<TaskFilter>(
    fk('taskFilter'),
    isAdmin ? 'all' : 'my'
  )
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState<{ id: string; subject: string } | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false)
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'activity_date')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'asc')
  const [dateFilter, setDateFilter] = usePersistentState(fk('dateFilter'), '')
  const [dateFrom, setDateFrom] = usePersistentState(fk('dateFrom'), '')
  const [dateTo, setDateTo] = usePersistentState(fk('dateTo'), '')
  // Owner scope from a dashboard deep-link (?owner_id=…). Overrides the My/All
  // toggle on arrival; cleared when the user toggles the filter.
  const [deepLinkOwnerId, setDeepLinkOwnerId] = useState<string | undefined>(undefined)

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  // Dashboard "Active Tasks" deep-link: ?status=open → only incomplete tasks
  const openOnly = searchParams.get('status') === 'open'
  // Exec Sales Dashboard "Follow-through" drill-down (?gap=overdue_tasks).
  const repGap = repGapFor('task', searchParams.get('gap'))

  // Apply dashboard deep-link filters once: a stat-card click carries the
  // dashboard's My/All scope + date range (activity_date) so this list matches
  // the card's number. (status=open is read above for the query + banner.)
  const dashboardAppliedRef = useRef(false)
  useEffect(() => {
    if (dashboardAppliedRef.current) return
    const isDashboardLink =
      searchParams.has('owner_id') ||
      searchParams.has('my') ||
      searchParams.has('from') ||
      searchParams.has('to') ||
      searchParams.has('status') ||
      searchParams.has('gap')
    if (!isDashboardLink) return
    dashboardAppliedRef.current = true
    // Owner: explicit ?owner_id (any rep) wins; ?my back-compat → current user.
    const me = impersonatedUser?.id || currentUser?.id
    const ownerId = searchParams.get('owner_id') || (searchParams.has('my') ? me : undefined)
    setDeepLinkOwnerId(ownerId)
    setTaskFilter(!!ownerId && ownerId === me ? 'my' : 'all')
    const from = searchParams.get('from') || ''
    const to = searchParams.get('to') || ''
    if (from || to) {
      setDateFilter('custom')
      setDateFrom(from)
      setDateTo(to)
    } else {
      setDateFilter('')
      setDateFrom('')
      setDateTo('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, isLoading } = useTasks({
    search: debouncedSearch,
    page,
    limit: pageSize,
    owner_id:
      deepLinkOwnerId ??
      (taskFilter === 'my' ? impersonatedUser?.id || currentUser?.id : undefined),
    assigned_by_id:
      !deepLinkOwnerId && taskFilter === 'assigned'
        ? impersonatedUser?.id || currentUser?.id
        : undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    date_filter: dateFilter || undefined,
    date_from: dateFilter === 'custom' && dateFrom ? dateFrom : undefined,
    date_to: dateFilter === 'custom' && dateTo ? dateTo : undefined,
    open: openOnly ? 'true' : undefined,
    gap: repGap,
  })
  const { data: stats } = useTaskStats()
  const updateMutation = useUpdateTask()
  const deleteMutation = useDeleteTask()
  const bulkDeleteMutation = useBulkDeleteTasks()
  const bulkUpdateMutation = useBulkUpdateTasks()
  const bulkAssignMutation = useBulkAssignTasks()
  const { toast } = useToast()
  const isReadOnly = useReadOnly()

  // One-click complete/reopen from the task list status icon. Completing a task
  // always opens the follow-up "create task" window (prefilled), matching the
  // detail-modal behavior; reopening does not prompt.
  const [followUpTask, setFollowUpTask] = useState<_Task | null>(null)
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false)

  const handleToggleComplete = async (task: _Task) => {
    if (task.task_source === 'rfp_task') return // RFP tasks complete on the RFP page
    const wasCompleted = task.status === 'Completed'
    try {
      await updateMutation.mutateAsync({
        id: task.id,
        data: { status: wasCompleted ? 'Not Started' : 'Completed' },
      })
      toast({
        title: wasCompleted ? 'Task reopened' : 'Task completed',
        description: task.subject,
      })
      if (!wasCompleted) {
        setFollowUpTask(task)
        setFollowUpModalOpen(true)
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to update task'),
        variant: 'destructive',
      })
    }
  }

  const {
    items: tasks,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = extract(data)

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return

    try {
      await deleteMutation.mutateAsync(taskToDelete.id)

      toast({
        title: 'Success',
        description: `Task "${taskToDelete.subject}" has been deleted.`,
      })

      setDeleteDialogOpen(false)
      setTaskToDelete(null)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete task')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === tasks.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(tasks.map((task) => task.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} task${idsArray.length === 1 ? '' : 's'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete tasks')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })

      toast({
        title: 'Success',
        description: `${selectedIds.size} task${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update tasks')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk assign
  const handleBulkAssign = async (ownerId: string, notifyOwner: boolean) => {
    try {
      await bulkAssignMutation.mutateAsync({
        ids: Array.from(selectedIds),
        owner_id: ownerId,
        notify_owner: notifyOwner,
      })

      toast({
        title: 'Success',
        description: `${selectedIds.size} task${selectedIds.size === 1 ? '' : 's'} assigned successfully.`,
      })

      setBulkAssignModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to assign tasks')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle sort toggle
  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )

  // Handle date filter change
  const handleDateFilter = useCallback(
    (filter: string) => {
      setDateFilter((prev) => (prev === filter ? '' : filter))
      if (filter !== 'custom') {
        setDateFrom('')
        setDateTo('')
      }
      firstPage()
    },
    [firstPage]
  )

  const handleClearDateFilter = useCallback(() => {
    setDateFilter('')
    setDateFrom('')
    setDateTo('')
    firstPage()
  }, [firstPage])

  // Handle CSV export
  const handleExportCSV = () => {
    // For now, export the current page of tasks
    // In a production app, you might want to fetch all tasks from the API
    exportTasksToCSV(tasks)
    toast({
      title: 'Export Successful',
      description: `Exported ${tasks.length} tasks to CSV`,
    })
  }

  // Define field configurations
  const tasksUpdateFields: BulkUpdateField[] = [
    { key: 'subject', label: 'Subject', type: 'text', placeholder: 'Enter new subject' },
    { key: 'activity_date', label: 'Due Date', type: 'date' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Not Started', label: 'Not Started' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Deferred', label: 'Deferred' },
      ],
    },
    {
      key: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { value: 'High', label: 'High' },
        { value: 'Medium', label: 'Medium' },
        { value: 'Low', label: 'Low' },
      ],
    },
  ]

  // Create columns with callbacks. RFP-sourced tasks (task_source === 'rfp_task')
  // deep-link to the RFP detail page instead of opening the task-detail modal,
  // because the modal only knows how to edit activities.tasks rows.
  const columns = useMemo(
    () =>
      createTasksColumns({
        onViewTask: (taskId: string) => {
          const task = tasks.find((t) => t.id === taskId)
          if (task?.task_source === 'rfp_task' && task.rfp_id) {
            navigate(`/rfps/${task.rfp_id}`)
            return
          }
          setSelectedTaskId(taskId)
          setDetailModalOpen(true)
        },
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === tasks.length && tasks.length > 0,
        onToggleComplete: handleToggleComplete,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, tasks, navigate]
  )

  const [columnVisibility, setColumnVisibility] = useColumnVisibility('tasks-column-visibility')
  const [columnOrder, setColumnOrder] = useColumnOrder('tasks-column-order')

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="task"
          onClearSelection={handleClearSelection}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          onBulkAssign={() => setBulkAssignModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
          isAssigning={bulkAssignMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="task" />
            <h1 className="text-3xl font-bold tracking-tight">Tasks</h1>
          </div>
          <p className="text-muted-foreground mt-1">Manage and track your tasks and to-dos</p>
          <GapFilterBanner
            gap={repGap}
            clearTo="/tasks"
            ownerName={deepLinkOwnerId ? 'selected rep' : undefined}
          />
          {openOnly && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                Filtered by: Open tasks
              </span>
              <Link to="/tasks" className="text-sm text-muted-foreground hover:text-foreground">
                Clear filter
              </Link>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['tasks']} label="tasks" />
          {canExport && (
            <Button variant="outline" aria-label="Export tasks to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button
            aria-label="Create new task"
            onClick={() => setCreateModalOpen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Cannot create tasks while viewing as another user' : undefined}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Task
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{total}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {tasks.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.in_progress ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.completed ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {taskFilter === 'my'
              ? 'My Tasks'
              : taskFilter === 'assigned'
                ? 'Tasks I Assigned'
                : 'All Tasks'}
          </CardTitle>
          <CardDescription>
            {taskFilter === 'assigned'
              ? 'Tasks you created and assigned to others'
              : 'View and manage your tasks'}
          </CardDescription>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Tabs
              value={taskFilter}
              onValueChange={(value) => {
                setDeepLinkOwnerId(undefined)
                setTaskFilter(value as TaskFilter)
                firstPage()
              }}
            >
              <TabsList>
                <TabsTrigger value="all" aria-label="Show all tasks">
                  All Tasks
                </TabsTrigger>
                <TabsTrigger value="my" aria-label="Show only my tasks">
                  My Tasks
                </TabsTrigger>
                <TabsTrigger value="assigned" aria-label="Show tasks I assigned to others">
                  Assigned
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <SearchWithRecent
              value={search}
              onChange={setSearch}
              placeholder="Search tasks..."
              ariaLabel="Search tasks"
              entityKey="tasks"
            />
            <ColumnPicker
              columns={columns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
            />
          </div>

          {/* Date filter bar */}
          <div
            className="flex flex-wrap items-center gap-2 pt-2"
            role="toolbar"
            aria-label="Filter tasks by due date"
          >
            <span className="text-sm text-muted-foreground mr-1">Due date:</span>
            {[
              { value: 'overdue', label: 'Overdue' },
              { value: 'today', label: 'Today' },
              { value: 'this_week', label: 'This Week' },
              { value: 'this_month', label: 'This Month' },
              { value: 'custom', label: 'Custom' },
            ].map((filter) => (
              <Button
                key={filter.value}
                variant={dateFilter === filter.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleDateFilter(filter.value)}
                aria-pressed={dateFilter === filter.value}
                aria-label={`Filter by ${filter.label}`}
              >
                {filter.label}
              </Button>
            ))}
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-2">
                <DatePicker
                  value={dateFrom}
                  onChange={(v) => {
                    setDateFrom(v ?? '')
                    firstPage()
                  }}
                  className="w-36 h-8"
                  aria-label="From date"
                />
                <span className="text-sm text-muted-foreground">to</span>
                <DatePicker
                  value={dateTo}
                  onChange={(v) => {
                    setDateTo(v ?? '')
                    firstPage()
                  }}
                  className="w-36 h-8"
                  aria-label="To date"
                />
              </div>
            )}
            {dateFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearDateFilter}
                aria-label="Clear date filter"
              >
                <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tasks found. Create your first task to get started.
            </div>
          ) : (
            <ResizableTable
              columns={columns}
              data={tasks}
              storageKey="tasks-column-widths"
              defaultColumnSizing={tasksDefaultColumnSizing}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              columnOrder={columnOrder}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          )}

          {/* Pagination */}
          {!isLoading && total > 0 && (
            <div className="flex items-center justify-between px-2 py-4 gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Rows per page</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v))
                      firstPage()
                    }}
                  >
                    <SelectTrigger className="h-8 w-[80px]" aria-label="Tasks per page">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[25, 50, 100, 200].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)}{' '}
                  of {total} tasks
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                    Previous
                  </Button>
                  <div className="flex items-center gap-1 text-sm">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <TaskDetailModal
        taskId={selectedTaskId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />

      <CreateTaskModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        ownerId={currentUser?.id}
      />

      {/* Follow-up task, prefilled from a just-completed task (one-click complete). */}
      {followUpTask && (
        <CreateTaskModal
          open={followUpModalOpen}
          onOpenChange={(open) => {
            setFollowUpModalOpen(open)
            if (!open) setFollowUpTask(null)
          }}
          defaultSubject={`Follow-up: ${followUpTask.subject}`}
          relatedToId={followUpTask.what_id}
          relatedToType={
            followUpTask.what_id?.startsWith('006')
              ? 'Opportunity'
              : followUpTask.what_id?.startsWith('001')
                ? 'Account'
                : followUpTask.what_id?.startsWith('500')
                  ? 'Ticket'
                  : followUpTask.what_type
          }
          relatedToName={followUpTask.what_name}
          ownerId={followUpTask.owner_id}
          defaultContacts={[
            ...(followUpTask.who_id && followUpTask.who_name
              ? [
                  {
                    id: followUpTask.who_id,
                    name: followUpTask.who_name,
                    type: (followUpTask.who_id.startsWith('00Q') ? 'Lead' : 'Contact') as
                      | 'Contact'
                      | 'Lead',
                  },
                ]
              : []),
            ...(followUpTask.related_contacts?.map((rc) => ({
              id: rc.relation_id,
              name: rc.relation_name,
              type: rc.relation_type as 'Contact' | 'Lead',
            })) || []),
          ]}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-task-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription id="delete-task-description">
              Are you sure you want to delete the task "{taskToDelete?.subject}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete task ${taskToDelete?.subject}`}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="task"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="task"
        fields={tasksUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />

      <BulkAssignModal
        open={bulkAssignModalOpen}
        onOpenChange={setBulkAssignModalOpen}
        selectedCount={selectedIds.size}
        entityType="task"
        onConfirm={handleBulkAssign}
        isAssigning={bulkAssignMutation.isPending}
      />
    </div>
  )
}
